<template>
  <div class="card-update-page">
    <card title="Edit Page">
      <div :disabled="inProgress">
        <fieldset class="form-group">
          <label for="pageName">Page Name</label>
          <input
            id="pageName"
            type="text"
            class="form-control form-control-lg"
            v-model="page.name"
            placeholder="Page Name"
          />
        </fieldset>

        <fieldset class="form-group">
          <label for="pageType">Page Type</label>
          <select v-model="page.type" id="pageType" class="form-control">
            <option value="place">Place</option>
            <option value="video">Video</option>
            <option value="card-grid">Card Grid</option>
            <option value="iframe">iFrame</option>
          </select>
        </fieldset>

        <fieldset class="form-group">
          <label for="pageAccessCode">Access PIN</label>
          <input
            id="pageAccessCode"
            type="text"
            inputmode="numeric"
            class="form-control form-control-lg"
            v-model.trim="page.accessCode"
            placeholder="Optional"
          />
        </fieldset>

        <fieldset class="form-group">
          <label for="pageHeading">
            Sidebar Heading
            <localised-field-indicator />
          </label>
          <input
            id="pageHeading"
            type="text"
            class="form-control form-control-lg"
            :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
            v-model="page.heading[activeLanguage.code]"
            placeholder="Page Heading"
          />
        </fieldset>

        <fieldset class="form-group">
          <label>
            Sidebar Description
            <localised-field-indicator />
          </label>
          <ck-editor
            v-model="page.description[activeLanguage.code]"
            :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
            :key="activeLanguage.code"
            :language="activeLanguage.code"
          />
        </fieldset>

        <fieldset v-if="page.type === 'iframe'" class="form-group">
          <label for="frameUrl">iFrame URL</label>
          <input
            id="frameUrl"
            type="url"
            class="form-control form-control-lg"
            v-model.trim="page.frameUrl"
            placeholder="https://example.com/"
            required
          />
          <small class="form-text text-muted">
            Must be a full http(s) URL. Some sites block embedding in iframes.
          </small>
        </fieldset>

        <fieldset v-if="page.type !== 'iframe'" class="form-group">
          <label for="qrUrl">QR Code URL</label>
          <input
            id="qrUrl"
            type="text"
            class="form-control form-control-lg"
            v-model="page.qrUrl"
            placeholder="https://domain.com/"
          />
        </fieldset>

        <fieldset v-if="page.type !== 'iframe'" class="form-group">
          <label for="qrMessage">
            QR Code Message
            <localised-field-indicator />
          </label>
          <input
            id="qrMessage"
            :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
            type="text"
            class="form-control form-control-lg"
            v-model="page.qrMessage[activeLanguage.code]"
            placeholder="A brief description of where the QR code will take the user."
          />
        </fieldset>

        <fieldset v-if="page.type !== 'iframe'" class="form-group">
          <label for="virtualTourUrl">Virtual Tour URL</label>
          <input
            id="virtualTourUrl"
            type="text"
            class="form-control form-control-lg"
            v-model="page.virtualTourUrl"
            placeholder="https://domain.com/"
          />
        </fieldset>

        <fieldset v-if="page.type !== 'iframe'" class="form-group">
          <label for="virtualTourMessage">
            Virtual Tour Button Label
            <localised-field-indicator />
          </label>
          <input
            id="virtualTourMessage"
            type="text"
            class="form-control form-control-lg"
            :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
            v-model="page.virtualTourButtonLabel[activeLanguage.code]"
            placeholder=""
          />
        </fieldset>

        <fieldset v-if="page.type !== 'iframe'" class="form-group">
          <label for="issuuEmbed">Issuu Embed Url</label>
          <input
            type="text"
            class="form-control form-control-lg"
            v-model="page.issuuEmbedUrl"
            placeholder=""
          />
        </fieldset>

        <fieldset
          class="form-group"
          v-if="
            ['place', 'video'].indexOf(page.type) !== -1 &&
              page.type !== 'iframe'
          "
        >
          <label>Select Minimap Image</label>
          <input-asset
            v-model="page.minimapImage"
            defaultCategory="minimapImage"
          ></input-asset>
        </fieldset>

        <fieldset v-if="page.type !== 'iframe'" class="form-group">
          <label>
            Select Background Image
            <localised-field-indicator />
          </label>
          <input-asset
            v-model="page.backgroundImage[activeLanguage.code]"
            defaultCategory="pageBackground"
          ></input-asset>
        </fieldset>

        <fieldset v-if="page.type !== 'iframe'" class="form-group">
          <label>Video Settings</label>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="page.backgroundVideoSettings.loop"
            />
            <span class="ml-2">Loop Video</span>
          </div>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="page.backgroundVideoSettings.showControls"
            />
            <span class="ml-2"
              >Show Controls<InfoBox
                >Show the video player controls (pause, mute).</InfoBox
              ></span
            >
          </div>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="page.backgroundVideoSettings.delayTags"
            />
            <span class="ml-2"
              >Delay Tags
              <InfoBox
                >Delay animating tags in until after video play back has
                finished.</InfoBox
              ></span
            >
          </div>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="page.backgroundVideoSettings.startMuted"
            />
            <span class="ml-2"
              >Start Muted
              <InfoBox
                >Start the video with the sound muted. Can be unmuted if "Show
                Controls" is checked.</InfoBox
              ></span
            >
          </div>
        </fieldset>

        <fieldset class="form-group">
          <InputTransitionVideos
            v-model="page.pageTransitionVideos"
          ></InputTransitionVideos>
        </fieldset>
      </div>
    </card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CkEditor from "@/components/CKEditor";
import InputAsset from "@/components/InputAsset";
import InputMultiSelect from "@/components/InputMultiSelect";
import InfoBox from "@/components/InfoBox";
import InputTransitionVideos from "@/components/InputTransitionVideos";

export default {
  name: "card-update-page",

  components: {
    CkEditor,
    InputAsset,
    InputMultiSelect,
    InfoBox,
    InputTransitionVideos
  },

  props: {
    inProgress: Boolean
  },

  data() {
    return {
      pagesToTransition: []
    };
  },

  watch: {
    loop() {
      // disable tag delay if we're looping the video
      if (this.page.backgroundVideoSettings.loop) {
        this.page.backgroundVideoSettings.delayTags = false;
      }
    },

    page: {
      handler() {
        // annoying fix that I dont understand why we need
        if (!this.page.pageTransitionVideos) {
          this.page.pageTransitionVideos = {};
        } else if (Object.keys(this.page.pageTransitionVideos).length) {
          this.pagesToTransition = Object.keys(this.page.pageTransitionVideos);
        }
      },
      immediate: true
    },

    pagesToTransition() {
      this.pagesToTransition.forEach(pageId => {
        if (!Object.keys(this.page.pageTransitionVideos).length) return;
        if (!Object.keys(this.page.pageTransitionVideos).includes(pageId)) {
          this.page.pageTransitionVideos[pageId] = null;
        }
      });
    }
  },

  computed: {
    ...mapGetters(["activeLanguage", "page", "pages"]),

    isBackgroundVideo() {
      const asset = this.page.backgroundImage[this.activeLanguage.code];
      if (asset) {
        const ext = asset.split(".").pop();

        switch (ext) {
          case "mp4":
          case "mov":
            return true;

          default:
            return false;
        }
      }

      return false;
    },

    loop() {
      return this.page.backgroundVideoSettings.loop;
    }
  }
};
</script>

<style lang="scss" scoped>
.card-update-page {
  padding-right: 15px;
  padding-left: 15px;
  position: relative;
  width: 100%;
  // z-index: 2;
}
</style>
